<script setup lang="ts">
import { ref, onMounted, computed, inject, watch, onUnmounted } from "vue";
import type { Ref } from "vue";
import { APIURL } from "@/defaults";
import { formatPercent, formatNumber, formatDate } from "@/helpers/formatters";
import type { PriceDataObject, StockDataObject, UserData, StockData, StockDataWeights, FundChartDataItem, FundChartDataItemParsed, HoldingDataObject, StockChartDataItem, StockChartDataItemParsed, PricesAfterHoursObjectParsed } from "@/interfaces/interfaces";
import ColoredNumber from "@/components/ColoredNumber.vue";
import FundNavItem from "@/components/FundNavItem.vue";
import TickerBar from "@/components/TickerBar.vue";
import { intervalStartDateRelativeTo } from "@/helpers/intervals";
import { parseDateString } from "@/helpers/dates";
import { calculatePerformanceIntervals } from "@/composables/usePerformanceIntervals";

const props = defineProps<{
  fund_id: string;
}>();

// Emit events to parent
const emit = defineEmits(['update:fund_id']);

// Inject shared data
const prices = inject('prices') as Ref<PriceDataObject | null>;
const stocks_injected = inject('stocks') as Ref<StockDataObject | null>;
const wsPubSub = inject('wsPubSub') as any;
const userData = inject('userData') as Ref<UserData | null>;
const injectedHoldings = inject('holdings') as Ref<HoldingDataObject | null>;

// State for funds
const funds = [
  { id: "205410", name: "Disruptive Tech", abbreviation: "OGAW" },
  { id: "205310", name: "Disruptive Tech Inst", abbreviation: "AIF" },
  { id: "206910", name: "Small & Mid Cap Tech", abbreviation: "SMC" },
  { id: "207610", name: "Clean Tech", abbreviation: "CLEAN" },
];

const currentFund: Ref<string> = ref(props.fund_id || "205410");

// State for holdings
interface StockWithPerformance {
  isin: string;
  name: string;
  weights: {[key: string]: number};
  performance_today: number;
  price: number;
  currency: string;
  afterhours_performance?: number;
  afterhours_price?: number;
  afterhours_timestamp?: string;
}

// Add chart data for fund performance
const chart: Ref<FundChartDataItemParsed[]> = ref([]);
const isChartLoading: Ref<boolean> = ref(false);

// Add Amundi Nasdaq 100 chart data
const amundiChart: Ref<StockChartDataItemParsed[]> = ref([]);
const isAmundiChartLoading: Ref<boolean> = ref(false);

// After hours prices
const pricesAfterHours: Ref<PricesAfterHoursObjectParsed | null> = ref(null);

// Ticker data for mobile scrolling ticker
const tickerData: Ref<Record<string, number | null>> = ref({
  '^NDX': null,
  '^GSPC': null,
  '^STOXX': null,
  '^HSI': null,
  'EURUSD=X': null
});

let tickerInterval: any = null;

// Fetch ticker data for mobile ticker
const fetchTickerData = async () => {
  const tickers = [
    { name: "NASDAQ-100", id: "^NDX" },
    { name: "S&P500", id: "^GSPC" },
    { name: "STOXX Europe 600", id: "^STOXX" },
    { name: "Hang Seng", id: "^HSI" },
    { name: "EUR/USD", id: "EURUSD=X" },
  ];

  for (const ticker of tickers) {
    const url = `${APIURL}/index/${ticker.id}/realtime?disable_currency_adjustment=True`;

    try {
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) {
        console.error(`Failed to fetch ticker data for ${ticker.id}: ${response.statusText}`);
        tickerData.value[ticker.id] = null;
        continue;
      }

      const data = await response.json();
      tickerData.value[ticker.id] = data.intraday_change || 0;
    } catch (error) {
      console.error(`Error fetching ticker ${ticker.id}:`, error);
      tickerData.value[ticker.id] = null;
    }
  }
};

// Handle realtime ticker updates
const handleTickerRealtimeUpdate = (message: any) => {
  if (message.type === "realtime_update" && message.ticker && typeof message.intraday_change === 'number') {
    if (tickerData.value.hasOwnProperty(message.ticker)) {
      tickerData.value[message.ticker] = message.intraday_change;
    }
  }
};

// Replace direct holdings with computed reference to the injected holdings
const fundHoldings = computed(() => {
  return injectedHoldings.value;
});

// Add computed property for cash weight
const cashWeight = computed(() => {
  if (!fundHoldings.value) {
    return null;
  }

  const cashHoldings = Object.values(fundHoldings.value.values).filter(
    (holding) => holding.type === 'BAR' || holding.type === 'VERBINDLICHKEITEN'
  );

  const cashWeightTotal = cashHoldings.reduce(
    (total, holding) => total + holding.booked_data.fund_proportion,
    0
  );

  const cashValueTotal = cashHoldings.reduce(
    (total, holding) => total + holding.booked_data.value_eur,
    0
  );

  return {
    weight: cashWeightTotal,
    value_eur: cashValueTotal,
  };
});

// Get fund start date based on fund ID
const getFundStartDate = (fund_id: string): Date => {
  if (fund_id === "205410" || fund_id === "205310") {
    return parseDateString("2021-09-01");
  }
  if (fund_id === "206910") {
    return parseDateString("2023-02-13");
  }
  if (fund_id === "207610") {
    return parseDateString("2024-03-01");
  }
  return parseDateString("2021-09-01");
};

// Computed for performance intervals
const performance_intervals = computed(() => {
  return calculatePerformanceIntervals(chart.value, null, getFundStartDate(currentFund.value));
});

// Computed for Amundi performance intervals
const amundi_performance_intervals = computed(() => {
  const fundStartDate = getFundStartDate(currentFund.value);
  return calculatePerformanceIntervals(amundiChart.value, latest_fund_date.value, fundStartDate);
});

// Latest fund date
const latest_fund_date = computed(() => {
  if (!chart.value || !chart.value.length) {
    return null;
  }
  return chart.value[chart.value.length - 1].date;
});

// Function to fetch fund chart data
const fetchFundChart = () => {
  isChartLoading.value = true;
  chart.value = [];

  fetch(`${APIURL}/fund/${currentFund.value}/chart`, { credentials: "include" })
    .then((r) => r.json())
    .then((r: FundChartDataItem[]) => {
      const parsed = r.map((x) => {
        return {...x, date: parseDateString(x.date)}
      });
      chart.value = parsed;
      isChartLoading.value = false;
    })
    .catch(err => {
      console.error("Failed to fetch fund chart data:", err);
      isChartLoading.value = false;
    });
};

// Function to fetch Amundi Nasdaq 100 chart data
const fetchAmundiChart = () => {
  isAmundiChartLoading.value = true;
  amundiChart.value = [];

  fetch(`${APIURL}/index/LU1681038243/chart`, { credentials: "include" })
    .then((r) => r.json())
    .then((r: StockChartDataItem[]) => {
      const parsed = r.map((x) => {
        return {...x, date: parseDateString(x.date)}
      }) as StockChartDataItemParsed[];
      amundiChart.value = parsed;
      isAmundiChartLoading.value = false;
    })
    .catch(err => {
      console.error("Failed to fetch Amundi chart data:", err);
      isAmundiChartLoading.value = false;
    });
};

// Fetch after hours prices
const fetchAfterHoursPrices = async () => {
  try {
    const response = await fetch(`${APIURL}/prices-afterhours`, { credentials: "include" });
    if (response.ok) {
      pricesAfterHours.value = await response.json();
      updateHoldings(); // Update holdings after fetching after hours prices
    } else {
      console.error('Failed to fetch after hours prices');
    }
  } catch (error) {
    console.error('Error fetching after hours prices:', error);
  }
};

// Calculate after hours performance
const calculateAfterHoursPerformance = (isin: string): number => {
  if (!pricesAfterHours.value || !prices.value) return 0;

  const afterHoursData = pricesAfterHours.value[isin];
  const priceData = prices.value[isin];

  if (!afterHoursData || !priceData || !priceData.previous_close) return 0;

  // Calculate performance as percentage change from previous close to after hours price
  return (afterHoursData.price / priceData.previous_close) - 1;
};

// Get formatted after hours timestamp
const getAfterHoursTimestamp = (isin: string): string => {
  if (!pricesAfterHours.value) return '';

  const afterHoursData = pricesAfterHours.value[isin];
  if (!afterHoursData) return '';

  try {
    const date = new Date(afterHoursData.timestamp);
    return date.toLocaleString('de-DE');
  } catch (e) {
    return afterHoursData.timestamp;
  }
};

const holdings: Ref<StockWithPerformance[]> = ref([]);
const sortBy: Ref<string> = ref('weight'); // 'weight', 'performance', 'name'
const sortDirection: Ref<number> = ref(-1); // 1 for ascending, -1 for descending

// Helper to get current fund key
const getCurrentFundKey = (): string => {
  return `fund_${currentFund.value}`;
};

// Computed sorted holdings
const sortedHoldings = computed(() => {
  if (!holdings.value.length) return [];

  return [...holdings.value].sort((a, b) => {
    let aValue, bValue;
    const currentFundKey = getCurrentFundKey();

    if (sortBy.value === 'weight') {
      // For weight, use the current fund's weight
      aValue = a.weights?.[currentFundKey] || 0;
      bValue = b.weights?.[currentFundKey] || 0;
    } else if (sortBy.value === 'performance') {
      // For performance, prioritize today's performance or fall back to after hours
      aValue = (a.performance_today !== 0) ? a.performance_today : (a.afterhours_performance || 0);
      bValue = (b.performance_today !== 0) ? b.performance_today : (b.afterhours_performance || 0);
    } else if (sortBy.value === 'price') {
      aValue = a.price;
      bValue = b.price;
    } else {
      aValue = a.name;
      bValue = b.name;
      return aValue.localeCompare(bValue) * sortDirection.value;
    }

    if (aValue > bValue) return 1 * sortDirection.value;
    if (aValue < bValue) return -1 * sortDirection.value;
    return 0;
  });
});

// Filtered holdings for current fund
const currentFundHoldings = computed(() => {
  const currentFundKey = getCurrentFundKey();
  return sortedHoldings.value.filter(stock =>
    stock.weights &&
    stock.weights[currentFundKey] !== undefined &&
    stock.weights[currentFundKey] > 0
  );
});

// Function to toggle sort
const toggleSort = (key: string) => {
  if (sortBy.value === key) {
    sortDirection.value *= -1;
  } else {
    sortBy.value = key;
    sortDirection.value = key === 'name' ? 1 : -1; // Default ascending for name, descending for others
  }
};

// Function to set current fund
const setCurrentFund = (fundId: string) => {
  currentFund.value = fundId;
  localStorage.setItem("currentFund", fundId);

  // Emit the update to parent component
  emit('update:fund_id', fundId);

  // Clear existing chart data to avoid showing stale information
  chart.value = [];
  amundiChart.value = [];

  // Set loading states
  isChartLoading.value = true;
  isAmundiChartLoading.value = true;

  // Fetch chart data for the selected fund
  fetchFundChart();
  fetchAmundiChart();
};

// Type guard to check if stock has weights
const hasWeights = (stock: StockData): stock is StockData => {
  return 'weights' in stock && stock.weights !== null;
};

// Function to update holdings for all funds
const updateHoldings = () => {
  if (!stocks_injected.value) return;

  holdings.value = Object.values(stocks_injected.value)
    .filter(hasWeights)
    .map(stock => {
      const stockWithWeights = stock as StockData;
      // Get price from the prices object using the stock's ISIN
      const priceData = prices.value?.[stock.isin];
      // Use close price from prices object, or default to 0 if not available
      const price = priceData?.close !== undefined ? priceData.close : 0;

      // Get after hours data
      const afterHoursData = pricesAfterHours.value?.[stock.isin];
      const afterhoursPrice = afterHoursData?.price;
      const afterhoursTimestamp = afterHoursData?.timestamp;

      return {
        isin: stock.isin,
        name: stock.name,
        weights: stockWithWeights.weights || {},
        performance_today: prices.value?.[stock.isin]?.performance_today || 0,
        price: price,
        currency: stock.currency || '',
        afterhours_performance: calculateAfterHoursPerformance(stock.isin),
        afterhours_price: afterhoursPrice,
        afterhours_timestamp: afterhoursTimestamp
      } as StockWithPerformance;
    });
};

// WebSocket handler
const handleWsMessage = (message: any) => {
  if (message.type === "funds_updated") {
    // Only update holdings for our current fund
    if (message.fund_id === currentFund.value) {
      updateHoldings();
    }
  }

  // Handle ticker updates
  handleTickerRealtimeUpdate(message);
};

// Lifecycle hooks
onMounted(async () => {
  fetchFundChart();
  fetchAmundiChart();
  fetchAfterHoursPrices();
  fetchAllFundCharts(); // Add this to fetch AuM and cashflow data

  if (wsPubSub) {
    wsPubSub.subscribe(handleWsMessage);
  }

  // Start fetching ticker data
  await fetchTickerData();
  tickerInterval = setInterval(fetchTickerData, 5 * 60 * 1000); // Update every 5 minutes
});

onUnmounted(() => {
  if (wsPubSub) {
    wsPubSub.unsubscribe(handleWsMessage);
  }
  if (tickerInterval) {
    clearInterval(tickerInterval);
  }
});

// Watch for changes in dependencies
watch([prices, stocks_injected], () => {
  updateHoldings();
}, { immediate: true });

// Separate watch for holdings to better debug updates
watch(injectedHoldings, (newValue) => {
  console.log(`MobileView: Holdings updated for fund ${currentFund.value}`);
  updateHoldings();
}, { immediate: true });

watch(() => props.fund_id, (newValue) => {
  if (newValue) {
    currentFund.value = newValue;

    // Clear existing data to avoid showing stale information
    chart.value = [];
    amundiChart.value = [];

    // Set loading states
    isChartLoading.value = true;
    isAmundiChartLoading.value = true;

    // Fetch new data for the selected fund
    fetchFundChart();
    fetchAmundiChart();
  }
});

// Loading indicator condition
const isLoading = computed(() => {
  const hasStocks = stocks_injected?.value && Object.keys(stocks_injected.value).length > 0;
  const hasPrices = prices?.value && Object.keys(prices.value).length > 0;
  return !hasStocks || !hasPrices;
});

// Return true if a stock is in the current fund
const isInCurrentFund = (stock: StockWithPerformance): boolean => {
  const fundKey = getCurrentFundKey();
  return stock.weights && stock.weights[fundKey] !== undefined && stock.weights[fundKey] > 0;
};

// Show all holdings or just current fund holdings
const showAllHoldings: Ref<boolean> = ref(false);

// Direct fetch functions if needed as fallback
const fetchStocks = () => {
  console.log("Directly fetching stocks");
  fetch(`${APIURL}/stocks`, { credentials: "include" })
    .then(r => {
      if (r.status !== 200) {
        throw new Error("Failed to fetch stocks");
      }
      return r.json();
    })
    .then(r => {
      const stocks_obj: StockDataObject = {};
      for (const s of r) {
        const isin = s.isin;
        stocks_obj[isin] = s;
      }

      if (stocks_injected) {
        stocks_injected.value = stocks_obj;
        console.log("Stocks directly loaded:", Object.keys(stocks_obj).length);
      }

      // Update holdings after stocks are loaded
      updateHoldings();
    })
    .catch(err => {
      console.error("Failed to directly fetch stocks:", err);
    });
};

// Add fund chart data for AuM and cashflow calculations
interface FundCharts {
  [index: string]: Ref<FundChartDataItemParsed[]>;
}

const fundCharts: FundCharts = {
  "205310": ref([]),
  "205410": ref([]),
  "206910": ref([]),
  "207610": ref([]),
};

const areAllFundChartsLoaded = ref(false);

// Fetch fund chart data for all funds
const fetchAllFundCharts = async () => {
  const fundIds = ["205310", "205410", "206910", "207610"];

  try {
    const promises = fundIds.map(async (fundId) => {
      const response = await fetch(`${APIURL}/fund/${fundId}/chart`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        fundCharts[fundId].value = data.map((item: any) => ({
          ...item,
          date: parseDateString(item.date)
        }));
      } else {
        console.error(`Failed to fetch chart data for fund ${fundId}`);
        fundCharts[fundId].value = [];
      }
    });

    await Promise.all(promises);
    areAllFundChartsLoaded.value = true;
  } catch (error) {
    console.error("Error fetching fund charts:", error);
    areAllFundChartsLoaded.value = false;
  }
};

// Computed property for total AuM across all funds
const totalAuM = computed(() => {
  if (!areAllFundChartsLoaded.value) return null;

  const aumByDate: { [date: string]: number } = {};

  for (const [fundId, chart] of Object.entries(fundCharts)) {
    for (const row of chart.value) {
      const dateKey = row.date.toISOString();
      if (!aumByDate[dateKey]) {
        aumByDate[dateKey] = 0;
      }
      aumByDate[dateKey] += row.fonds_value;
    }
  }

  const sortedDates = Object.keys(aumByDate).sort();
  const latestDate = sortedDates[sortedDates.length - 1];

  return latestDate ? aumByDate[latestDate] : null;
});

// Computed property for individual fund AuMs
const individualFundAuMs = computed(() => {
  if (!areAllFundChartsLoaded.value) return {};

  const result: { [fundId: string]: number | null } = {};

  for (const [fundId, chart] of Object.entries(fundCharts)) {
    if (chart.value.length > 0) {
      const latestData = chart.value[chart.value.length - 1];
      result[fundId] = latestData.fonds_value;
    } else {
      result[fundId] = null;
    }
  }

  return result;
});

// Computed property for individual fund 1-month AuM changes
const individualFundAuMChanges = computed(() => {
  if (!areAllFundChartsLoaded.value) return {};

  const result: { [fundId: string]: number | null } = {};

  for (const [fundId, chart] of Object.entries(fundCharts)) {
    if (chart.value.length === 0) {
      result[fundId] = null;
      continue;
    }

    const latestData = chart.value[chart.value.length - 1];
    const oneMonthAgo = intervalStartDateRelativeTo(latestData.date, "1m");

    // Find the closest data point to one month ago
    let monthAgoValue = null;
    for (let i = chart.value.length - 1; i >= 0; i--) {
      if (chart.value[i].date <= oneMonthAgo) {
        monthAgoValue = chart.value[i].fonds_value;
        break;
      }
    }

    if (monthAgoValue === null) {
      result[fundId] = null;
    } else {
      result[fundId] = latestData.fonds_value - monthAgoValue;
    }
  }

  return result;
});



// Computed property for total 1-month AuM change across all funds
const totalAuMChange = computed(() => {
  if (!areAllFundChartsLoaded.value) return null;

  let totalChange = 0;
  let hasAnyData = false;

  for (const fundId of Object.keys(individualFundAuMChanges.value)) {
    const change = individualFundAuMChanges.value[fundId];
    if (change !== null) {
      totalChange += change;
      hasAnyData = true;
    }
  }

  return hasAnyData ? totalChange : null;
});

// Computed property for latest date from all fund charts
const latestAuMDate = computed(() => {
  if (!areAllFundChartsLoaded.value) return null;

  let latestDate: Date | null = null;

  for (const [fundId, chart] of Object.entries(fundCharts)) {
    if (chart.value.length > 0) {
      const fundLatestDate = chart.value[chart.value.length - 1].date;
      if (!latestDate || fundLatestDate > latestDate) {
        latestDate = fundLatestDate;
      }
    }
  }

  return latestDate;
});

</script>

<template>
  <div class="mobile-view">
    <!-- Market Performance Ticker at the very top -->
    <div class="mobile-ticker-bar">
      <div class="ticker-content">
        <span class="ticker-item">NASDAQ-100: <ColoredNumber v-if="tickerData['^NDX'] !== null" :value="tickerData['^NDX']" :percent="true" /><span v-else>…</span></span>
        <span class="ticker-item">S&P500: <ColoredNumber v-if="tickerData['^GSPC'] !== null" :value="tickerData['^GSPC']" :percent="true" /><span v-else>…</span></span>
        <span class="ticker-item">STOXX Europe 600: <ColoredNumber v-if="tickerData['^STOXX'] !== null" :value="tickerData['^STOXX']" :percent="true" /><span v-else>…</span></span>
        <span class="ticker-item">Hang Seng: <ColoredNumber v-if="tickerData['^HSI'] !== null" :value="tickerData['^HSI']" :percent="true" /><span v-else>…</span></span>
        <span class="ticker-item">EUR/USD: <ColoredNumber v-if="tickerData['EURUSD=X'] !== null" :value="tickerData['EURUSD=X']" :percent="true" /><span v-else>…</span></span>
        <!-- Duplicate for seamless scrolling -->
        <span class="ticker-item">NASDAQ-100: <ColoredNumber v-if="tickerData['^NDX'] !== null" :value="tickerData['^NDX']" :percent="true" /><span v-else>…</span></span>
        <span class="ticker-item">S&P500: <ColoredNumber v-if="tickerData['^GSPC'] !== null" :value="tickerData['^GSPC']" :percent="true" /><span v-else>…</span></span>
        <span class="ticker-item">STOXX Europe 600: <ColoredNumber v-if="tickerData['^STOXX'] !== null" :value="tickerData['^STOXX']" :percent="true" /><span v-else>…</span></span>
        <span class="ticker-item">Hang Seng: <ColoredNumber v-if="tickerData['^HSI'] !== null" :value="tickerData['^HSI']" :percent="true" /><span v-else>…</span></span>
        <span class="ticker-item">EUR/USD: <ColoredNumber v-if="tickerData['EURUSD=X'] !== null" :value="tickerData['EURUSD=X']" :percent="true" /><span v-else>…</span></span>
      </div>
    </div>

    <!-- Mobile Header with Logo -->
    <div class="mobile-header">
      <div class="mobile-logo">
        <img alt="TEQ Capital" src="@/assets/teq-logo-horizontal.svg" />
      </div>
    </div>

    <!-- Loading indicator -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading data...</p>
    </div>

    <!-- Main content when data is loaded -->
    <div v-else class="mobile-content">
      <!-- Fund Navigation using FundNavItem component -->
      <div class="fund-nav">
        <div
          v-for="fund in funds"
          :key="fund.id"
          class="fund-nav-wrapper"
          @click="setCurrentFund(fund.id)"
        >
          <FundNavItem
            :fund_id="fund.id"
            :fund_name="fund.name"
            :active="fund.id === currentFund"
            :mobile="true"
          />
        </div>
      </div>

      <!-- Performance Module -->
      <div class="performance-module">
        <h3 class="performance-module-title">Performance</h3>
        <div v-if="isChartLoading" class="performance-loading">
          <div class="loading-spinner small"></div>
          <p>Loading performance data...</p>
        </div>
        <div v-else-if="chart.length === 0" class="performance-no-data">
          <p>No performance data available</p>
        </div>
        <div v-else class="performance-metrics">
          <div class="performance-metric">
            <div class="metric-label">YTD</div>
            <div class="metric-value">
              <ColoredNumber :value="performance_intervals.ytd" :percent="true" />
            </div>
          </div>
          <div class="performance-metric">
            <div class="metric-label">1Y</div>
            <div class="metric-value">
              <ColoredNumber :value="performance_intervals['1y']" :percent="true" />
            </div>
          </div>
          <div class="performance-metric">
            <div class="metric-label">2Y</div>
            <div class="metric-value">
              <ColoredNumber :value="performance_intervals['2y']" :percent="true" />
            </div>
          </div>
          <div class="performance-metric">
            <div class="metric-label">3Y</div>
            <div class="metric-value">
              <ColoredNumber :value="performance_intervals['3y']" :percent="true" />
            </div>
          </div>
          <div class="performance-metric">
            <div class="metric-label">All Time</div>
            <div class="metric-value">
              <ColoredNumber :value="performance_intervals.total" :percent="true" />
            </div>
          </div>
        </div>
        <div v-if="latest_fund_date" class="performance-date">
          As of {{ formatDate(latest_fund_date, true) }}
        </div>

        <!-- Amundi Nasdaq 100 Performance Section -->
        <div class="amundi-performance-section">
          <h4 class="amundi-performance-title">Amundi NASDAQ 100</h4>
          <div v-if="isAmundiChartLoading" class="performance-loading">
            <div class="loading-spinner small"></div>
            <p>Loading benchmark data...</p>
          </div>
          <div v-else-if="amundiChart.length === 0" class="performance-no-data">
            <p>No benchmark data available</p>
          </div>
          <div v-else class="performance-metrics">
            <div class="performance-metric">
              <div class="metric-label">YTD</div>
              <div class="metric-value">
                <ColoredNumber :value="amundi_performance_intervals.ytd" :percent="true" />
              </div>
            </div>
            <div class="performance-metric">
              <div class="metric-label">1Y</div>
              <div class="metric-value">
                <ColoredNumber :value="amundi_performance_intervals['1y']" :percent="true" />
              </div>
            </div>
            <div class="performance-metric">
              <div class="metric-label">2Y</div>
              <div class="metric-value">
                <ColoredNumber :value="amundi_performance_intervals['2y']" :percent="true" />
              </div>
            </div>
            <div class="performance-metric">
              <div class="metric-label">3Y</div>
              <div class="metric-value">
                <ColoredNumber :value="amundi_performance_intervals['3y']" :percent="true" />
              </div>
            </div>
            <div class="performance-metric">
              <div class="metric-label">All Time</div>
              <div class="metric-value">
                <ColoredNumber :value="amundi_performance_intervals.total" :percent="true" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Holdings List -->
      <div class="holdings-container">
        <div class="holdings-header">
          <h2>Holdings</h2>
          <!-- Cash Weight Display -->
          <div v-if="cashWeight" class="cash-weight-display">
            <div class="cash-weight-item">
              <img src="/icons/euro.svg" alt="" class="cash-icon" />
              <span class="cash-label">Cash:</span>
              <span class="cash-value">{{ formatPercent(cashWeight.weight, 2) }}</span>
              <span class="cash-amount" v-if="cashWeight.value_eur">{{ formatNumber(cashWeight.value_eur, 0) }} EUR</span>
            </div>
          </div>

          <!-- Toggle for showing all holdings or just current fund -->
          <div class="holdings-toggle">
            <label>
              <input type="checkbox" v-model="showAllHoldings">
              Show all holdings
            </label>
          </div>

          <div class="sort-controls">
            <button
              @click="toggleSort('name')"
              :class="{'active': sortBy === 'name'}"
            >
              Name {{ sortBy === 'name' ? (sortDirection > 0 ? '↑' : '↓') : '' }}
            </button>
            <button
              @click="toggleSort('weight')"
              :class="{'active': sortBy === 'weight'}"
            >
              Weight {{ sortBy === 'weight' ? (sortDirection > 0 ? '↑' : '↓') : '' }}
            </button>
            <button
              @click="toggleSort('performance')"
              :class="{'active': sortBy === 'performance'}"
            >
              Perf {{ sortBy === 'performance' ? (sortDirection > 0 ? '↑' : '↓') : '' }}
            </button>
          </div>
        </div>

        <div class="holdings-list">
          <div v-if="(showAllHoldings ? sortedHoldings : currentFundHoldings).length === 0" class="no-holdings">
            <p>No holdings found for this fund.</p>
          </div>

          <div
            v-else
            v-for="stock in (showAllHoldings ? sortedHoldings : currentFundHoldings)"
            :key="stock.isin"
            class="holding-item"
            :class="{'highlight-row': isInCurrentFund(stock)}"
          >
            <div class="holding-top-row">
              <div class="holding-name">{{ stock.name }}</div>
              <div class="holding-price">
                <template v-if="stock.performance_today !== 0 || !stock.afterhours_price">
                  {{ stock.price > 0 ? formatNumber(stock.price, 2) + ' ' + stock.currency : '-' }}
                </template>
                <template v-else>
                  <span class="afterhours-price" :title="`After hours: ${getAfterHoursTimestamp(stock.isin)}`">
                    {{ formatNumber(stock.afterhours_price || 0, 2) }} {{ stock.currency }}*
                  </span>
                </template>
              </div>
            </div>
            <div class="holding-details">
              <!-- Show weights for current fund or all funds based on toggle -->
              <div class="holding-weights">
                <span v-if="!showAllHoldings" class="holding-weight">
                  {{ formatPercent(stock.weights[getCurrentFundKey()] || 0, 2) }}
                </span>
                <template v-else>
                  <div class="all-weights">
                    <template v-for="fund in funds" :key="fund.id">
                      <span v-if="stock.weights[`fund_${fund.id}`]" class="fund-weight" :class="{'current-fund': fund.id === currentFund}">
                        {{ fund.abbreviation }}: {{ formatPercent(stock.weights[`fund_${fund.id}`] || 0, 1) }}
                      </span>
                    </template>
                  </div>
                </template>
              </div>
              <div class="holding-performance">
                <!-- Show regular performance if available, otherwise after hours -->
                <template v-if="stock.performance_today !== 0">
                  <ColoredNumber
                    :value="stock.performance_today"
                    :percent="true"
                  />
                </template>
                <template v-else-if="stock.afterhours_performance">
                  <span class="afterhours-indicator">AH</span>
                  <ColoredNumber
                    :value="stock.afterhours_performance"
                    :percent="true"
                    :title="`After Hours: ${getAfterHoursTimestamp(stock.isin)}`"
                  />
                </template>
                <template v-else>
                  <span>-</span>
                </template>
              </div>
            </div>
          </div>
        </div>

        <div v-if="pricesAfterHours" class="afterhours-legend">
          * After hours prices shown when intraday not available
        </div>
      </div>

      <!-- AuMs Module -->
      <div class="au-m-cashflow-module">
        <h3 class="au-m-cashflow-module-title">Assets under Management</h3>
                 <div v-if="!areAllFundChartsLoaded" class="au-m-cashflow-loading">
           <div class="loading-spinner small"></div>
           <p>Loading AuM data...</p>
         </div>
                 <div v-else class="au-m-cashflow-content">
           <div class="au-m-cashflow-item individual-funds-section">
             <div class="individual-funds-table">
               <div class="funds-table-header">
                 <span class="header-fund">Fund</span>
                 <span class="header-current">AuM (EUR)</span>
                 <span class="header-change">1M Change (EUR)</span>
               </div>
               <div v-for="fund in funds" :key="fund.id" class="individual-fund-row">
                 <span class="fund-abbreviation">{{ fund.abbreviation }}</span>
                 <span class="fund-aum-value">
                   <span v-if="individualFundAuMs[fund.id] !== null">{{ formatNumber(individualFundAuMs[fund.id], 0) }}</span>
                   <span v-else>Loading...</span>
                 </span>
                 <span class="fund-change-value">
                   <ColoredNumber v-if="individualFundAuMChanges[fund.id] !== null" :value="individualFundAuMChanges[fund.id]" :decimals="0" />
                   <span v-else>-</span>
                 </span>
               </div>
               <div class="funds-table-total">
                 <span class="total-label">Total</span>
                 <span class="total-aum-value">
                   <span v-if="totalAuM !== null">{{ formatNumber(totalAuM, 0) }}</span>
                   <span v-else>Loading...</span>
                 </span>
                 <span class="total-change-value">
                   <ColoredNumber v-if="totalAuMChange !== null" :value="totalAuMChange" :decimals="0" />
                   <span v-else>-</span>
                 </span>
               </div>
             </div>
             <div v-if="latestAuMDate" class="au-m-date-statement">
               As of {{ formatDate(latestAuMDate) }}
             </div>
           </div>
         </div>
      </div>
    </div>
  </div>
</template>

<style>
.mobile-view {
  display: flex;
  flex-direction: column;
  max-width: 100%;
  padding: 10px;
  box-sizing: border-box;
  font-size: 14px;
  background-color: #f5f5f5;
  min-height: 100vh;
  position: static;
  height: auto;
  overflow: visible;
  -webkit-overflow-scrolling: auto;
}

/* Mobile content container */
.mobile-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
}

/* Loading indicator */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  flex: 1;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(226, 159, 118, 0.3);
  border-radius: 50%;
  border-top-color: rgb(226, 159, 118);
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 15px;
}

.loading-spinner.small {
  width: 20px;
  height: 20px;
  border-width: 2px;
  margin-bottom: 8px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.no-holdings {
  padding: 20px;
  text-align: center;
  color: #666;
}

/* Mobile Header */
.mobile-header {
  display: flex;
  justify-content: center;
  padding: 15px 0;
  margin-bottom: 15px;
  position: relative;
  z-index: 20;
  background-color: #f5f5f5;
}

.mobile-logo {
  max-width: 180px;
}

.mobile-logo img {
  width: 100%;
  height: auto;
}

/* Fund Navigation */
.fund-nav {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  position: relative;
  z-index: 15;
}

/* Wrapper for FundNavItem to handle click events */
.fund-nav-wrapper {
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.fund-nav-wrapper:active {
  transform: scale(0.98);
}

/* Performance Module */
.performance-module {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 12px;
  margin-bottom: 20px;
}

.performance-module-title {
  margin: 0 0 10px 0;
  font-size: 16px;
}

.performance-metrics {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.performance-metric {
  flex: 1;
  min-width: 60px;
  text-align: center;
  padding: 8px 4px;
  background-color: #f9f9f9;
  border-radius: 6px;
}

.metric-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.metric-value {
  font-weight: 600;
  font-size: 14px;
}

.performance-date {
  margin-top: 10px;
  font-size: 12px;
  color: #666;
  text-align: right;
}

.performance-loading, .performance-no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px 0;
}

.performance-loading p, .performance-no-data p {
  margin: 0;
  font-size: 12px;
  color: #666;
}

/* Amundi Performance Section */
.amundi-performance-section {
  margin-top: 15px;
  border-top: 1px solid #eee;
  padding-top: 12px;
}

.amundi-performance-title {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #666;
}

/* AuMs and Cash Flow Module */
.au-m-cashflow-module {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 12px;
  margin-bottom: 20px;
}

.au-m-cashflow-module-title {
  margin: 0 0 10px 0;
  font-size: 16px;
}

.au-m-date-statement {
  margin-top: 10px;
  font-size: 12px;
  color: #666;
  text-align: right;
}

.au-m-cashflow-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px 0;
}

.au-m-cashflow-loading p {
  margin: 0;
  font-size: 12px;
  color: #666;
}

.au-m-cashflow-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.au-m-cashflow-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.au-m-cashflow-item:last-child {
  border-bottom: none;
}

.au-m-cashflow-label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.au-m-cashflow-value {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  text-align: right;
}

 .individual-funds-section {
   flex-direction: column;
   align-items: stretch;
 }

 .individual-funds-table {
   margin-top: 8px;
   display: flex;
   flex-direction: column;
   gap: 2px;
 }

 .funds-table-header {
   display: grid;
   grid-template-columns: 1fr 1fr 1fr;
   gap: 8px;
   padding: 6px 8px;
   background-color: #e9e9e9;
   border-radius: 4px;
   font-size: 11px;
   font-weight: 600;
   color: #555;
 }

 .header-fund,
 .header-current,
 .header-change {
   text-align: center;
 }

 .header-fund {
   text-align: left;
 }

 .header-current {
   text-align: right;
 }

 .header-change {
   text-align: right;
 }

 .individual-fund-row {
   display: grid;
   grid-template-columns: 1fr 1fr 1fr;
   gap: 8px;
   align-items: center;
   padding: 6px 8px;
   background-color: #f9f9f9;
   border-radius: 4px;
 }

 .fund-abbreviation {
   font-size: 12px;
   color: #666;
   font-weight: 600;
   text-align: left;
 }

 .fund-aum-value {
   font-size: 12px;
   font-weight: 600;
   text-align: right;
 }

 .fund-change-value {
   font-size: 12px;
   font-weight: 600;
   text-align: right;
 }

 .funds-table-total {
   display: grid;
   grid-template-columns: 1fr 1fr 1fr;
   gap: 8px;
   align-items: center;
   padding: 8px 8px;
   background-color: #e9e9e9;
   border-radius: 4px;
   margin-top: 4px;
   border-top: 2px solid #ccc;
 }

 .total-label {
   font-size: 12px;
   color: #333;
   font-weight: 700;
   text-align: left;
 }

 .total-aum-value {
   font-size: 12px;
   font-weight: 700;
   text-align: right;
   color: #333;
 }

 .total-change-value {
   font-size: 12px;
   font-weight: 700;
   text-align: right;
   color: #333;
 }

/* Holdings */
.holdings-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.holdings-header {
  padding: 12px;
  border-bottom: 1px solid #eee;
  background: #fff;
  z-index: 10;
}

.holdings-header h2 {
  margin: 0 0 10px 0;
  font-size: 16px;
}

.holdings-toggle {
  margin-bottom: 10px;
  font-size: 13px;
}

.holdings-toggle label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.sort-controls {
  display: flex;
  gap: 8px;
  padding-bottom: 4px;
}

.sort-controls button {
  background: #f5f5f5;
  border: none;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
}

.sort-controls button:active {
  background: #e0e0e0;
}

.sort-controls button.active {
  background: #e0e0e0;
  font-weight: bold;
}


.holding-item {
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
}

.holding-item.highlight-row {
  background-color: rgba(226, 159, 118, 0.1);
}

.holding-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.holding-name {
  font-weight: 600;
}

.holding-price {
  font-size: 12px;
  color: #333;
  font-weight: 600;
  text-align: right;
}

.afterhours-price {
  color: #666;
  font-style: italic;
}

.holding-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.holding-weights {
  font-size: 12px;
  color: #666;
}

.all-weights {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 180px;
}

.fund-weight {
  font-size: 10px;
  background: #f0f0f0;
  padding: 1px 4px;
  border-radius: 3px;
}

.fund-weight.current-fund {
  background: rgba(226, 159, 118, 0.3);
  font-weight: 600;
}

.holding-performance {
  font-weight: 600;
  text-align: right;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.afterhours-indicator {
  font-size: 9px;
  background: #666;
  color: white;
  padding: 0px 2px;
  border-radius: 2px;
}

.afterhours-legend {
  padding: 8px 12px;
  font-size: 10px;
  color: #666;
  font-style: italic;
  border-top: 1px solid #eee;
}

/* Media query for very small screens */
@media (max-width: 320px) {
  .mobile-view {
    padding: 5px;
  }

  .performance-metrics {
    flex-wrap: wrap;
  }

  .performance-metric {
    flex: 1 0 40%;
    margin-bottom: 8px;
  }
}

/* Add safe area insets for iOS devices */
@supports (padding: max(0px)) {
  .mobile-view {
    padding-left: max(10px, env(safe-area-inset-left));
    padding-right: max(10px, env(safe-area-inset-right));
    padding-bottom: max(10px, env(safe-area-inset-bottom));
    padding-top: max(10px, env(safe-area-inset-top));
  }
}

/* Mobile scrolling ticker bar styles */
.mobile-ticker-bar {
  margin: -10px -10px 15px -10px; /* Extend to full width, move to very top, add bottom margin */
  height: 32px;
  background: #333;
  color: #fff;
  font-size: 11px;
  line-height: 32px;
  overflow: hidden;
  white-space: nowrap;
  position: relative;
}

.ticker-content {
  display: inline-block;
  padding-left: 10%; /* Start from left border*/
  animation: scroll-left 60s linear infinite;
}

.ticker-item {
  display: inline;
  margin-right: 20px;
}

@keyframes scroll-left {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-100%);
  }
}

/* Responsive ticker bar for very small screens */
@media (max-width: 380px) {
  .mobile-ticker-bar {
    font-size: 10px;
  }

  .ticker-item {
    margin-right: 8px; /* Further reduced from 15px */
  }
}

/* Fix for iOS Safari 100vh issue */
@supports (-webkit-touch-callout: none) {
  .mobile-view {
    min-height: -webkit-fill-available;
    height: auto;
  }
}

/* Cash Weight Display */
.cash-weight-display {
  margin: 10px 0;
  padding: 8px 10px;
  background-color: #f9f9f9;
  border-radius: 6px;
  border: 1px solid #eee;
}

.cash-weight-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cash-icon {
  width: 16px;
  height: 16px;
}

.cash-label {
  font-weight: 600;
  color: #333;
}

.cash-value {
  font-weight: 600;
  color: #333;
}

.cash-amount {
  font-size: 12px;
  color: #666;
  margin-left: auto;
}
</style>
